import { BackendError } from '@genial/services';
import { selector, selectorFamily } from 'recoil';

import { getAllTags } from '../../../pages/BatchTicket/services/tag';
import { TagItemProp, Tags } from '../../../pages/BatchTicket/utils/types';
import { tagsState } from '../atom';
import { normalizeTagData } from '../normalize';

export const GetAllTagsByAdvisorSelector = selectorFamily<
  Tags | undefined | null,
  { advisorId?: number }
>({
  key: 'GetAllTagsByAdvisorSelector',
  get:
    ({ advisorId }) =>
    async () => {
      try {
        const { data } = await getAllTags(advisorId);

        const dataNormalized = normalizeTagData(data);

        return {
          list: dataNormalized,
          added: {},
          removed: {},
        };
      } catch (error) {
        const backendError = error as BackendError;
        if (backendError.statusCode === 404) {
          return { list: [], added: {}, removed: {} };
        }

        throw error;
      }
    },
});

export const addAndLinkTagSelector = selector({
  key: 'addAndLinkTagSelector',
  set: ({ set, get }, value) => {
    const currentTags = get(tagsState);
    const { customerId: itemId, ...rest } = value as TagItemProp;
    const tagExists = currentTags.list.find((tag) => tag.tagId === rest.tagId);

    const updatedTagList = tagExists ? currentTags.list : [...currentTags.list, rest];

    const updatedTagListItemsAdded = {
      ...currentTags.added,
      [itemId]: [...(currentTags.added[itemId] || []), rest],
    };

    const updatedRemovedTags = {
      ...currentTags.removed,
      [itemId]: (currentTags.removed[itemId] || []).filter(
        (removedTag) => removedTag.tagId !== rest.tagId
      ),
    };

    const data = {
      list: updatedTagList,
      added: updatedTagListItemsAdded,
      removed: updatedRemovedTags,
    };

    set(tagsState, data);
  },
  get: ({ get }) => get(tagsState),
});

export const updateTagLabelSelector = selector({
  key: 'updateTagLabelSelector',
  set: ({ set, get }, value) => {
    const currentTags = get(tagsState);
    const { tagId, tagName } = value as TagItemProp;

    const updatedTagList = currentTags?.list.map((tag) =>
      tag.tagId === tagId ? { ...tag, tagName } : tag
    );

    const updatedTagListItemsAdded = Object.fromEntries(
      Object.entries(currentTags?.added || {}).map(([itemId, tags]) => [
        itemId,
        tags.map((tag) => (tag.tagId === tagId ? { ...tag, tagName } : tag)),
      ])
    );

    const data = {
      list: updatedTagList || [],
      added: updatedTagListItemsAdded,
      removed: currentTags.removed,
    };

    set(tagsState, data);
  },
  get: ({ get }) => get(tagsState),
});

export const removeTagSelector = selector({
  key: 'removeTagSelector',
  set: ({ set, get }, tagIdToRemove) => {
    const currentTags = get(tagsState);

    const updatedTagList = currentTags.list.filter((tagItem) => tagItem.tagId !== tagIdToRemove);

    const updatedTagListItemsAdded = Object.fromEntries(
      Object.entries(currentTags.added).map(([itemId, tags]) => [
        itemId,
        tags.filter((tag) => tag.tagId !== tagIdToRemove),
      ])
    );

    const data = {
      list: updatedTagList,
      added: updatedTagListItemsAdded,
      removed: currentTags.removed,
    };

    set(tagsState, data);
  },
  get: ({ get }) => get(tagsState),
});

export const linkUnlinkTagItemSelector = selector({
  key: 'linkUnlinkTagItemSelector',
  set: ({ set, get }, value) => {
    const currentTags = get(tagsState);

    const { customerId: itemId, ...tag } = value as TagItemProp;

    const updatedTagListItemsRemoved = {
      ...currentTags.removed,
      [itemId]: [...(currentTags.removed[itemId] || []), tag],
    };

    const updatedAddedTags = {
      ...currentTags.added,
      [itemId]: (currentTags.added[itemId] || []).filter(
        (addedTag) => addedTag.tagId !== tag.tagId
      ),
    };

    const data = {
      list: currentTags.list,
      added: updatedAddedTags,
      removed: updatedTagListItemsRemoved,
    };

    set(tagsState, data);
  },
  get: ({ get }) => get(tagsState),
});
